/**
 * @fileoverview Column Shelf — vertical panel of hidden table columns as draggable chips.
 * Each chip shows the metric label and its live grand total. Chips drag into the
 * table header row to add the column; headers drag onto the shelf to remove.
 * Chips also support activate-to-append via click or Enter/Space as a non-drag fallback.
 * @module shared/components/ui/ColumnShelf
 */

import { ReactElement, useEffect, useRef, KeyboardEvent, PointerEvent } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { EyeOff, GripVertical } from 'lucide-react'
import { cn } from '@/shared/utils/cn'
import { SHELF_DROPPABLE_ID, chipDndId } from '@/shared/hooks/useColumnShelfDnd'

/** A hidden column rendered as a draggable chip. */
export interface ColumnShelfChip {
  /** Column id (bare, without dnd prefix). */
  id: string
  /** Display label (newlines already collapsed). */
  label: string
  /** Formatted live grand total for the metric. */
  value: React.ReactNode
}

/** A metric category grouping chips under a caption. */
export interface ColumnShelfSection {
  /** Stable section key (e.g. 'ads', 'costs'). */
  id: string
  /** Category caption shown above the section's chips. */
  label: string
  /** Chips for this category's hidden columns, in canonical column order. */
  chips: readonly ColumnShelfChip[]
}

export interface ColumnShelfProps {
  /** Non-empty metric categories with their hidden-column chips. */
  sections: readonly ColumnShelfSection[]
  /** True while a header is dragged over the shelf (highlights as remove zone). */
  isDropTarget: boolean
  /**
   * True while ANY header drag is in progress (not just hovering the shelf) —
   * surfaces the drop-to-hide banner so the gesture is discoverable.
   */
  isDragActive?: boolean
  /** Banner text shown while a header drag is active (e.g. "Drop here to hide"). */
  dropHint?: string
  /** Panel heading. */
  title: string
  /** Shown when every column is visible. */
  emptyHint: string
  /**
   * Append a column when its chip is clicked or activated via Enter/Space —
   * the non-drag fallback.
   */
  onAddColumn: (columnId: string) => void
  /**
   * Disables the drop zone and chip dragging while the shelf is closed but
   * still mounted for the width animation.
   */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Drop-enabled shelf listing hidden columns as draggable chips.
 *
 * @example
 * ```tsx
 * <ColumnShelf sections={sections} isDropTarget={dnd.isShelfDropTarget}
 *              title="More columns" emptyHint="All columns are shown"
 *              onAddColumn={handleAddColumn} />
 * ```
 */
export function ColumnShelf({
  sections,
  isDropTarget,
  isDragActive = false,
  dropHint,
  title,
  emptyHint,
  onAddColumn,
  disabled = false,
  className,
}: ColumnShelfProps): ReactElement {
  const { setNodeRef } = useDroppable({ id: SHELF_DROPPABLE_ID, disabled })

  return (
    <div
      ref={setNodeRef}
      role="region"
      aria-label={title}
      data-testid="column-shelf"
      data-drop-target={isDropTarget ? 'true' : undefined}
      className={cn(
        // Frameless at rest (a visible border reads as panel-in-panel); the
        // drop highlight uses a ring (box-shadow) so no border width exists
        // for any global border-color rule to paint.
        'flex flex-col gap-1.5 rounded-xl p-2 transition-colors',
        isDropTarget && 'ring-1 ring-ring bg-ring/10',
        className
      )}
    >
      <span className="px-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </span>
      {isDragActive && dropHint && (
        <div
          data-testid="shelf-drop-hint"
          aria-hidden="true"
          className={cn(
            'flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-ring/50 bg-ring/5 px-2 py-3 text-xs text-muted-foreground transition-colors',
            isDropTarget && 'border-solid border-ring bg-ring/15 text-foreground'
          )}
        >
          <EyeOff className="h-3.5 w-3.5 shrink-0" />
          <span>{dropHint}</span>
        </div>
      )}
      {sections.length === 0 ? (
        <span className="px-1 pb-1 text-xs text-muted-foreground">{emptyHint}</span>
      ) : (
        sections.map((section, index) => (
          <div
            key={section.id}
            className={cn('flex flex-col gap-1.5', index > 0 && 'mt-1.5')}
            data-testid={`shelf-section-${section.id}`}
          >
            <span className="px-1 text-[9px] font-medium uppercase tracking-wider text-muted-foreground/70">
              {section.label}
            </span>
            {section.chips.map(chip => (
              <ShelfChip key={chip.id} chip={chip} onAddColumn={onAddColumn} disabled={disabled} />
            ))}
          </div>
        ))
      )}
    </div>
  )
}

function ShelfChip({
  chip,
  onAddColumn,
  disabled = false,
}: {
  chip: ColumnShelfChip
  onAddColumn: (columnId: string) => void
  disabled?: boolean
}): ReactElement {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: chipDndId(chip.id),
    disabled,
  })

  const wasDragging = useRef(false)

  useEffect(() => {
    if (isDragging) {
      wasDragging.current = true
    }
  }, [isDragging])

  function handleClick(): void {
    if (disabled) return
    if (wasDragging.current) {
      wasDragging.current = false
      return
    }
    onAddColumn(chip.id)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onAddColumn(chip.id)
    }
  }

  // Reset the stale drag flag on each new pointer-down so an Escape-cancelled
  // drag doesn't swallow the next genuine click.
  function handlePointerDown(event: PointerEvent<HTMLDivElement>): void {
    wasDragging.current = false
    listeners?.onPointerDown?.(event)
  }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      // onKeyDown and onPointerDown MUST stay after {...listeners} to override
      // dnd-kit's keyboard drag-start (a dead end for chips) with Enter/Space-to-append,
      // and to reset the wasDragging flag before dnd-kit receives pointer events.
      onKeyDown={handleKeyDown}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      title={chip.label}
      data-testid={`shelf-chip-${chip.id}`}
      className={cn(
        'flex cursor-grab touch-none items-center justify-between gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-2 text-sm transition-colors',
        'hover:border-ring/60 hover:bg-ring/5 active:cursor-grabbing',
        isDragging && 'opacity-40'
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5 text-foreground/90">
        <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
        <span className="truncate">{chip.label}</span>
      </span>
      <span className="shrink-0 font-mono text-xs text-muted-foreground">{chip.value}</span>
    </div>
  )
}
